import React, { forwardRef, ReactNode, useLayoutEffect, useState } from "react"
import styled, { css } from "styled-components"
import { theme } from "../../../theme"
import { ButtonPrimary, ButtonProps, ButtonText } from "../../atoms/button"
import { Dialog as DialogMaterial, DialogContent, DialogProps as DialogPropsMaterial } from "@mui/material"
import { useWindowSize } from "../../../hooks"
import { useIsOverflow } from "../../../hooks/useIsOverflow"
import { DialogSize } from "./DialogSize"

export type DialogStepperProps = Pick<DialogPropsMaterial, "onClose" | "className"> & {
  title: string
  children: ReactNode
  open: boolean
  actions?: ReactNode
  secondaryButton?: ButtonProps
  primaryButton?: ButtonProps
  size?: DialogSize
  onExited?: () => void
}

export const DialogStepper = forwardRef<HTMLDivElement, DialogStepperProps>((props, ref) => {
  const {
    title,
    secondaryButton,
    primaryButton,
    actions,
    size = DialogSize.small,
    children,
    open,
    onClose,
    onExited,
    className,
  } = props
  const [showScroll, setShowScroll] = useState(false)
  const [contentElement, setContentElement] = useState<HTMLDivElement>()

  const [, windowHeight] = useWindowSize()
  const isOverflow = useIsOverflow(contentElement)

  const refCallback = (element: HTMLDivElement) => {
    if (element !== null) setContentElement(element)
  }

  useLayoutEffect(() => {
    setShowScroll(contentElement != null && contentElement.clientHeight < contentElement.scrollHeight)
  }, [contentElement, windowHeight, isOverflow])

  return (
    <DialogContainer
      ref={ref}
      $size={size}
      open={open}
      transitionDuration={theme.transitionDuration}
      onClose={onClose}
      maxWidth={false}
      TransitionProps={{ onExited: onExited }}
      className={className}
    >
      <HeadingContainer $scroll={showScroll}>
        <HeadingText>{title}</HeadingText>
      </HeadingContainer>
      <CustomDialogContent ref={refCallback} dividers={true}>
        {children}
      </CustomDialogContent>
      <DialogActions $scroll={showScroll}>
        {actions}
        {secondaryButton && <ButtonText {...secondaryButton} />}
        {primaryButton && <ButtonPrimary {...primaryButton} />}
      </DialogActions>
    </DialogContainer>
  )
})

const sizeStyle = {
  [DialogSize.small]: css`
    width: 600px;
  `,
  [DialogSize.large]: css`
    width: 790px;
  `,
}

const DialogContainer = styled(DialogMaterial)<{ $size: DialogSize }>`
  .MuiDialog-paper {
    ${(props) => sizeStyle[props.$size]}
  }
`

const CustomDialogContent = styled(DialogContent)`
  overflow-y: auto;

  &.MuiDialogContent-root {
    padding: 0;
  }
`

const HeadingContainer = styled.div<{ $scroll: boolean }>`
  height: 40px;
  padding: ${theme.spacing.m};
`

const HeadingText = styled.div`
  font-weight: 400;
  font-size: ${theme.fontSize.m};
  color: ${theme.color.black4};
`

const DialogActions = styled.div<{ $scroll: boolean }>`
  padding: ${theme.spacing.lx};
  min-height: ${(p) => (p.$scroll ? "88px" : theme.spacing.xxl)};
  display: flex;
  align-items: ${(p) => (p.$scroll ? "center" : "start")};
  justify-content: flex-end;
  margin-top: auto;
  column-gap: ${theme.spacing.m};
`
